/**
 * sow-embed-bridge — the validated postMessage contract between the SOW
 * chromeless embed route (app origin, framed) and the `www` marketing-site
 * modal shell (ADR-080 Amendment 2, §A2.3).
 *
 * The cross-origin iframe is the security boundary: the Same-Origin Policy
 * walls the journey off from the marketing site, and this narrow, explicitly
 * validated message schema is the ONLY channel across it. Both sides validate
 * the counterparty origin on every message; anything else is dropped silently.
 * No `eval`-style channels, no structured-clone of functions, no wildcard
 * targetOrigin for data-bearing messages.
 *
 * Handshake: the embed announces `hello` to each allowed parent origin (the
 * browser only delivers to the origin that actually matches the parent, so a
 * non-www framer never receives it); the shell replies `init`. Only after a
 * validated `init` does the embed know its real parent origin and start
 * posting `resize` / `close` / `success` to that origin exclusively.
 *
 * Pure functions only — the React page owns the window wiring; this module is
 * unit-tested in test/sow-embed-bridge.test.ts.
 */

/** The `www` origins allowed to frame the embed route. Must stay in lockstep
 *  with the `frame-ancestors` CSP in portal-subscriber/public/_headers. */
export const EMBED_ALLOWED_PARENT_ORIGINS = [
  'https://nexanomics.com',
  'https://www.nexanomics.com',
] as const;

/** The two chromeless embeds this shell can present. */
export type SowEmbedKind = 'sow' | 'signin';

/** Named top-window destinations the shell may navigate to on the embed's
 *  request. NAMED, not a free path — the shell maps each to a known app-origin
 *  URL, so no arbitrary path ever crosses the boundary (ADR-092 D5). */
export type SowTopDestination = 'portal' | 'sign-in' | 'forgot-password';

/**
 * Messages the embed sends to the `www` shell.
 *
 * ADR-092 D5 — the Dashlane vector fix. Before, in-frame "Already have an
 * account? Sign in" links were `<a target="_blank">` to the REAL top-level
 * login page, where a password manager could autofill + auto-submit a saved
 * credential (silent wrong-account login). Those links are gone. Instead:
 *   - `switch` swaps the framed embed IN PLACE (sow ⇄ signin) — the modal stays
 *     open, no new tab, no top-level login page is ever opened in the background.
 *   - `navigate-top` asks the shell to navigate the TOP window to a NAMED portal
 *     page (the few flows that genuinely need a full page: forgot-password, the
 *     temp-password continue, the post-sign-in portal). A user-initiated top
 *     navigation to the intended page is not the silent-popup vector.
 */
export type SowEmbedOutbound =
  | { type: 'nexa-sow:hello' }
  | { type: 'nexa-sow:resize'; height: number }
  | { type: 'nexa-sow:close' }
  | { type: 'nexa-sow:success' }
  | { type: 'nexa-sow:switch'; to: SowEmbedKind }
  | { type: 'nexa-sow:navigate-top'; to: SowTopDestination };

/** Messages the embed accepts from the `www` shell. */
export type SowEmbedInbound = { type: 'nexa-sow:init' };

/**
 * Is `origin` an allowed `www` parent? Exact string match against the
 * allowlist — no prefix/suffix matching (a `https://nexanomics.com.evil.com`
 * framer must never pass). `extra` lets the dev server admit localhost
 * without widening the production allowlist.
 */
export function isAllowedParentOrigin(
  origin: string,
  extra: readonly string[] = [],
): boolean {
  return (
    (EMBED_ALLOWED_PARENT_ORIGINS as readonly string[]).includes(origin) ||
    extra.includes(origin)
  );
}

/**
 * Strictly parse an inbound message payload. Returns the typed message or
 * `null` for anything malformed — unknown types, missing fields, primitives,
 * arrays, prototype tricks. Origin validation is the caller's job (it lives
 * on the MessageEvent, not the payload); this validates shape only.
 */
export function parseInboundMessage(data: unknown): SowEmbedInbound | null {
  if (typeof data !== 'object' || data === null || Array.isArray(data)) return null;
  const type = (data as { type?: unknown }).type;
  if (type === 'nexa-sow:init') return { type: 'nexa-sow:init' };
  return null;
}
